import React from "react";

import { Button } from "../../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import {
  buildPlacementEmbedSnippet,
  formatEmbedModeLabel,
  formatFormKindLabel,
  formatPurposeLabel,
} from "./publicFormsConfig";

function buildPlacementTitle(placement, formName) {
  const rawName = (placement.name || "").trim();
  const lowerName = rawName.toLowerCase();
  const lowerFormName = (formName || "").trim().toLowerCase();
  if (!rawName) {
    return placement.embed_mode === "standalone" ? "Standalone-Seite" : "Standard-Einsatzort";
  }
  if (
    lowerName === "standard"
    || lowerName === "standardformular"
    || (lowerFormName && lowerName === lowerFormName)
  ) {
    return placement.embed_mode === "standalone" ? "Standalone-Seite" : "Standard-Einsatzort";
  }
  return rawName;
}

export default function PublicFormPlacementsList({
  placements,
  onCreate,
  onEdit,
  onCopyPlacementKey,
  onCopySnippet,
  widgetEmbedEnabled,
  formName,
  formKind,
  baseUrl,
  variant = "cards",
  showEmbeddingDetails = true,
  showTechnicalDetails = true,
}) {
  const widgetNotice = widgetEmbedEnabled
    ? "Website-Einbettung ist für Einsatzorte verfügbar und wird zentral im Bereich Einbettung verwaltet."
    : "Website-Einbettung ist in diesem Tarif nicht aktiv. Einsatzorte können vorbereitet werden; nutzbar werden sie mit dem Widget-Modul.";

  if (variant === "table") {
    return (
      <Card className="border-border/70 bg-card/80 shadow-sm">
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>Einsatzorte</CardTitle>
              <CardDescription>
                Verwalten Sie Einsatzorte getrennt von Snippets und technischen Details.
              </CardDescription>
            </div>
            <Button type="button" onClick={onCreate} className="w-full sm:w-auto">
              Einsatzort hinzufügen
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-xl border border-dashed border-border/80 p-3 text-sm text-muted-foreground">
            {widgetNotice}
          </div>
          <div className="rounded-xl border border-border/70 bg-card/60">
            <div className="hidden grid-cols-[minmax(0,1.5fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_160px_120px_120px] gap-3 border-b border-border/70 px-4 py-3 text-xs font-medium text-muted-foreground lg:grid">
              <div>Name</div>
              <div>Zweck</div>
              <div>Quelle</div>
              <div>Einbettungsart</div>
              <div>Status</div>
              <div>Aktionen</div>
            </div>
            <div className="divide-y divide-border/70">
              {placements.map((placement) => (
                <div
                  key={placement.id}
                  className="grid gap-3 px-4 py-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_160px_120px_120px] lg:items-center"
                >
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground lg:hidden">Name</div>
                    <div className="font-medium">{buildPlacementTitle(placement, formName)}</div>
                    <div className="text-xs text-muted-foreground">
                      CTA: {placement.cta_label || "Nicht gesetzt"}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground lg:hidden">Zweck</div>
                    <div>{formatPurposeLabel(placement.purpose_key)}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground lg:hidden">Quelle</div>
                    <div>{placement.source_label || "Nicht gesetzt"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground lg:hidden">Einbettungsart</div>
                    <div>{formatEmbedModeLabel(placement.embed_mode)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground lg:hidden">Status</div>
                    <div>{placement.is_active ? "Aktiv" : "Inaktiv"}</div>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row lg:justify-end">
                    <Button type="button" variant="outline" className="w-full lg:w-auto" onClick={() => onEdit(placement)}>
                      Bearbeiten
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/70 bg-card/80 shadow-sm">
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Einsatzorte</CardTitle>
            <CardDescription>
              Ein Einsatzort beschreibt, wo und mit welcher Absicht dieses Formular verwendet wird.
            </CardDescription>
            <div className="mt-2 text-xs text-muted-foreground">
              Einsatzorte verknüpfen ein Formular mit Zweck, CTA, Quelle und später dem Widget-Kontext.
            </div>
          </div>
          <Button type="button" onClick={onCreate} className="w-full sm:w-auto">
            Einsatzort hinzufügen
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-xl border border-dashed border-border/80 p-3 text-sm text-muted-foreground">
          {widgetNotice}
        </div>

        <div className="space-y-3">
          {placements.map((placement) => {
            const placementTitle = buildPlacementTitle(placement, formName);
            return (
              <div key={placement.id} className="rounded-xl border border-border/70 bg-card/60 p-4 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 space-y-3">
                    <div className="space-y-1">
                      <div className="font-medium">{placementTitle}</div>
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span className="rounded-full border border-border/70 bg-background/70 px-2 py-1">
                          Status: {placement.is_active ? "Aktiv" : "Inaktiv"}
                        </span>
                        <span className="rounded-full border border-border/70 bg-background/70 px-2 py-1">
                          Modus: {formatEmbedModeLabel(placement.embed_mode)}
                        </span>
                        <span className="rounded-full border border-border/70 bg-background/70 px-2 py-1">
                          Zweck: {formatPurposeLabel(placement.purpose_key)}
                        </span>
                        {placement.submit_test_mode ? (
                          <span className="rounded-full border border-amber-500/50 bg-amber-50 px-2 py-1 text-amber-900">
                            Testmodus aktiv – Mails werden unterdrückt
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div>Formular: {formName || "Unbekannt"}</div>
                      <div>Typ: {formatFormKindLabel(formKind)}</div>
                      <div>Quelle: {placement.source_label || "Nicht gesetzt"}</div>
                    </div>
                    {showTechnicalDetails ? (
                      <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-2">
                        <div className="mb-1 text-xs font-medium text-foreground/80">
                          Technischer Schlüssel
                        </div>
                        <div className="break-all font-mono text-xs text-muted-foreground">
                          {placement.placement_key}
                        </div>
                      </div>
                    ) : null}
                    {widgetEmbedEnabled && showEmbeddingDetails ? (
                      <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-3 space-y-2">
                        <div className="text-sm font-medium">Website-Einbettung</div>
                        <div className="text-xs text-muted-foreground">
                          Dieses Snippet bindet das Formular auf einer Website ein.
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Bitte nur auf Websites verwenden, deren Datenschutz- und Impressumsangaben korrekt hinterlegt sind.
                        </div>
                        <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-border/60 bg-background px-3 py-2 font-mono text-xs text-muted-foreground">
                          {buildPlacementEmbedSnippet(baseUrl, placement.placement_key)}
                        </pre>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex w-full flex-col gap-2 sm:w-auto sm:min-w-[180px]">
                    {widgetEmbedEnabled && showEmbeddingDetails ? (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => onCopySnippet(buildPlacementEmbedSnippet(baseUrl, placement.placement_key))}
                      >
                        Snippet kopieren
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => onCopyPlacementKey(placement.placement_key)}
                    >
                      Schlüssel kopieren
                    </Button>
                    <Button type="button" variant="outline" className="w-full" onClick={() => onEdit(placement)}>
                      Bearbeiten
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
